import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistance } from 'date-fns';
import { assignmentsService, busesService, sessionsService } from '../../services';
import { StatsCard } from './StatsCard';
import { RecentAssignments } from './RecentAssignments';

export const DashboardPage: React.FC = () => {
  const [endingSessionId, setEndingSessionId] = useState<string | null>(null);

  // Handle end session
  const handleEndSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to end this session?')) {
      return;
    }

    try {
      setEndingSessionId(sessionId);
      await sessionsService.endSession(sessionId);
      await sessionsQuery.refetch();
    } catch (error) {
      console.error('Failed to end session:', error);
      alert('Failed to end session. Please try again.');
    } finally {
      setEndingSessionId(null);
    }
  };

  // Fetch all data
  const assignmentsQuery = useQuery({
    queryKey: ['assignments'],
    queryFn: () => assignmentsService.getAssignments(),
    refetchInterval: 10000, // Refresh every 10 seconds (was 5s)
  });

  const busesQuery = useQuery({
    queryKey: ['buses'],
    queryFn: () => busesService.getBuses(),
    refetchInterval: 30000, // Refresh every 30 seconds (was 10s - buses rarely change)
  });

  const sessionsQuery = useQuery({
    queryKey: ['sessions', 'active'],
    queryFn: () => sessionsService.getActiveSessions(),
    refetchInterval: 10000, // Refresh every 10 seconds (was 3s)
  });

  const isLoading = assignmentsQuery.isLoading || busesQuery.isLoading || sessionsQuery.isLoading;

  // Calculate stats
  const totalAssignments = assignmentsQuery.data?.length || 0;
  const totalBuses = busesQuery.data?.length || 0;
  const activeSessions = sessionsQuery.data?.length || 0;

  const assignedAssignments =
    assignmentsQuery.data?.filter((a) => a.status === 'ASSIGNED').length || 0;
  const inProgressAssignments =
    assignmentsQuery.data?.filter((a) => a.status === 'IN_PROGRESS').length || 0;
  const completedAssignments =
    assignmentsQuery.data?.filter((a) => a.status === 'COMPLETED').length || 0;

  // Get recent assignments (last 5)
  const recentAssignments = assignmentsQuery.data
    ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your bus management system</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Assignments"
          value={totalAssignments}
          icon="📋"
          color="blue"
        />
        <StatsCard
          title="Total Buses"
          value={totalBuses}
          icon="🚌"
          color="green"
        />
        <StatsCard
          title="Active Sessions"
          value={activeSessions}
          icon="🟢"
          color="purple"
        />
        <StatsCard
          title="Completed"
          value={completedAssignments}
          icon="✅"
          color="emerald"
        />
      </div>

      {/* Assignment Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Assignment Status</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Assigned</span>
              <span className="text-2xl font-bold text-blue-600">{assignedAssignments}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">In Progress</span>
              <span className="text-2xl font-bold text-orange-600">{inProgressAssignments}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Completed</span>
              <span className="text-2xl font-bold text-emerald-600">{completedAssignments}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <a
              href="/assignments/new"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition"
            >
              <span className="text-2xl">➕</span>
              <div>
                <p className="font-medium text-gray-900">New Assignment</p>
                <p className="text-xs text-gray-500">Create an assignment</p>
              </div>
            </a>
            <a
              href="/buses/new"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-green-50 transition"
            >
              <span className="text-2xl">🚌</span>
              <div>
                <p className="font-medium text-gray-900">Add Bus</p>
                <p className="text-xs text-gray-500">Register a new bus</p>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      {sessionsQuery.data && sessionsQuery.data.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Active Sessions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Session ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Driver
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Bus ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Journey
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Started
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Last Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sessionsQuery.data.map((session: any) => {
                  const lastPositionText = session.lastPositionAt
                    ? formatDistance(new Date(session.lastPositionAt), new Date(), { addSuffix: true })
                    : 'No positions yet';

                  const isStale = session.lastPositionAt &&
                    new Date().getTime() - new Date(session.lastPositionAt).getTime() > 5 * 60 * 1000;

                  const isEnding = endingSessionId === session.sessionId;

                  return (
                    <tr key={session.sessionId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-mono text-gray-600">
                        {session.sessionId.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{session.driverId}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{session.busId}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{session.journeyId}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(session.startedAt).toLocaleString()}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isStale ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                        {lastPositionText} {isStale && '⚠️'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => handleEndSession(session.sessionId)}
                          disabled={isEnding}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 text-xs font-medium transition"
                        >
                          {isEnding ? 'Ending...' : 'End'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Assignments */}
      {recentAssignments && recentAssignments.length > 0 && (
        <RecentAssignments assignments={recentAssignments} />
      )}
    </div>
  );
};
